import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Version } from "./apps-types";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { Clear, Done, Launch } from "@mui/icons-material";
import { green, red } from "@mui/material/colors";
import { Link as RouterLink } from "react-router-dom";

export default function UploadAppSummary(props: {
  name?: string;
  uploadedVersion?: Version | undefined | null;
  status: number;
  open: boolean;
  setOpen: any;
  loading: boolean;
}) {
  const { t } = useTranslation();

  const handleCloseDialog = () => {
    props.setOpen(false);
  };

  return (
    <Box>
      <Dialog
        open={props.open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!props.loading && props.status >= 400 && props.status <= 500 ? (
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Stack alignItems="center" justifyContent="center" spacing={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Avatar sx={{ bgcolor: red[500], width: 80, height: 80 }}>
                      <Clear sx={{ width: 50, height: 50 }} />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" textAlign="center">
                    {t("Upload failed!")}
                  </Typography>
                  <Typography variant="body1">{t("Retry.")}</Typography>
                </Stack>
              </Grid>
            </Grid>
          ) : (props.uploadedVersion === undefined ||
              props.uploadedVersion === null) &&
            props.loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                height="100%x"
              >
                <CircularProgress size={80} thickness={3} />
                <Typography variant="h5" textAlign="center">
                  {t("Uploading...")}
                </Typography>
              </Stack>
            </Box>
          ) : (
            props.uploadedVersion !== null &&
            props.uploadedVersion !== undefined &&
            !props.loading &&
            props.status >= 200 &&
            props.status < 300 && (
              <Box>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mb: 2 }}
                >
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      spacing={2}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Avatar
                          sx={{ bgcolor: green[500], width: 80, height: 80 }}
                        >
                          <Done sx={{ width: 50, height: 50 }} />
                        </Avatar>
                      </Box>
                      <Typography variant="h4">
                        {t("Upload completed!")}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    alignItems="center"
                    justifyContent="center"
                    display="flex"
                  >
                    <Button
                      variant="outlined"
                      component={RouterLink}
                      to={`/applications/${
                        props.name !== undefined && props.name
                      }`}
                      startIcon={<Launch />}
                    >
                      {t("See application details")}
                    </Button>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  sx={{ pl: 2, pr: 2 }}
                >
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("Information")}
                      </Typography>
                    </Divider>
                  </Grid>

                  <Grid item xs={12} sm={7}>
                    <TextField
                      margin="dense"
                      id="name"
                      label={t("Application Name")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={props.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      margin="dense"
                      id="version"
                      label={t("Version")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={props.uploadedVersion.versionNumber}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      margin="dense"
                      id="bundle"
                      label={t("Bundle Identifier")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={props.uploadedVersion.bundleId}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      margin="dense"
                      id="bundle"
                      label={t("Platform")}
                      type="text"
                      fullWidth
                      variant="standard"
                      value={
                        props.uploadedVersion.platform === "IOS"
                          ? "iOS"
                          : "Android"
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
